import { useEffect, useState, useCallback, Fragment } from 'react'
import ResellerShell from './ResellerShell'
import NetRateConfirmForm from '../../components/NetRateConfirmForm'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Reseller "To book" worklist — PAID custom-trip rounds whose tee time still
 * needs to be secured:
 *   - awaiting_confirmation: net-rate / request-mode course → book by hand, then
 *     Confirm here (records the real time + optional course ref, emails customer).
 *   - deferred: connected course whose sheet hasn't opened → the hourly cron
 *     auto-books when it opens, but the reseller can lock it in early.
 */
const money = (n, cur = 'CAD') => (n == null ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const fmtDate = v => { if (!v) return '—'; const d = new Date(String(v).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }

const thc = { textAlign: 'left', padding: '8px 12px', fontSize: 11.5, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }
const td = { padding: '10px 12px', fontSize: 13, color: '#334155', verticalAlign: 'top' }

export default function ToBookList() {
  const { t } = useLanguage()
  const [rounds, setRounds] = useState([])
  const [hotels, setHotels] = useState([])
  const [canIssueRefunds, setCanIssueRefunds] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [openId, setOpenId] = useState(null)             // round componentId whose confirm form is open
  const [openHotelKey, setOpenHotelKey] = useState(null) // "bookingId:sequence" of the open hotel confirm
  const [form, setForm] = useState({ teeTime: '', ref: '' })
  const [busy, setBusy] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    tournamentApi.get('/portal/reseller/to-book')
      .then(r => { setRounds(r.data?.rounds || []); setHotels(r.data?.hotels || []); setCanIssueRefunds(!!r.data?.canIssueRefunds); setErr(null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  // A round row is either a booking component (pay-in-full park) or a quote item
  // (deposit/split net-rate, no booking). Key + confirm route differ by source.
  const rowKey = (r) => (r.source === 'quote' ? `q${r.quoteItemId}` : `c${r.componentId}`)

  function openConfirm(row) {
    setOpenId(rowKey(row))
    // Pre-fill the tee time with the requested window's start (strip the ~ marker).
    setForm({ teeTime: String(row.requestedTeeTime || '').replace(/^~/, ''), ref: '' })
    setErr(null)
  }

  async function confirm(row) {
    const teeTime = (form.teeTime || '').trim()
    if (!teeTime) { setErr(t('sp_tobook_teetime_required', 'Enter the confirmed tee time.')); return }
    const key = rowKey(row)
    setBusy(key); setErr(null)
    try {
      const url = row.source === 'quote'
        ? `/portal/reseller/quotes/${row.quoteId}/rounds/${row.quoteItemId}/confirm-net-rate`
        : `/portal/reseller/bookings/${row.bookingId}/rounds/${row.roundIndex}/confirm-request`
      await tournamentApi.post(url, { teeTime, ref: form.ref || null })
      setOpenId(null); setForm({ teeTime: '', ref: '' })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  return (
    <ResellerShell title={t('sp_tobook_title', 'Tee times & hotels to book')}>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0, marginBottom: 16, maxWidth: 760, lineHeight: 1.5 }}>
        {t('sp_tobook_subtitle', 'Paid rounds and net-rate hotels that still need to be secured by hand. Connected courses auto-book when their sheet opens; book or confirm the rest here.')}
      </p>

      {err && <div style={{ marginBottom: 12, color: '#991b1b', fontSize: 13 }}>{err}</div>}

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>{t('sp_loading', 'Loading…')}</div>
      ) : (rounds.length === 0 && hotels.length === 0) ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: '28px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, textAlign: 'center' }}>
          {t('sp_tobook_empty', 'Nothing waiting to be booked or confirmed.')}
        </div>
      ) : (
        <>
          {hotels.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>{t('sp_tobook_hotels_heading', 'Hotels to confirm')}</h3>
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={thc}>{t('sp_tobook_col_hotel', 'Hotel')}</th>
                      <th style={thc}>{t('sp_tobook_col_customer', 'Customer')}</th>
                      <th style={thc}>{t('sp_tobook_col_stay', 'Stay')}</th>
                      <th style={thc}>{t('sp_tobook_col_rooms', 'Rooms')}</th>
                      <th style={{ ...thc, textAlign: 'right' }}>{t('sp_tobook_col_net', 'Net')}</th>
                      <th style={thc}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotels.map(h => {
                      const key = `${h.bookingId}:${h.sequence}`
                      const open = openHotelKey === key
                      return (
                        <Fragment key={key}>
                          <tr style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={td}>
                              <div style={{ fontWeight: 600, color: '#0f172a' }}>{h.hotelName || '—'}</div>
                              {h.quoteNumber && <div style={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'monospace' }}>{h.quoteNumber}{h.quoteName ? ` · ${h.quoteName}` : ''}</div>}
                            </td>
                            <td style={{ ...td, color: '#64748b' }}>{h.customer || '—'}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(h.checkInDate)} → {fmtDate(h.checkOutDate)}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{h.numRooms != null ? h.numRooms : '—'}</td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(h.parkedCost, h.currency)}</td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button onClick={() => setOpenHotelKey(open ? null : key)}
                                style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #0f172a', borderRadius: 6, background: open ? '#fff' : '#0f172a', color: open ? '#0f172a' : '#fff', cursor: 'pointer' }}>
                                {open ? t('sp_cancel', 'Cancel') : t('sp_tobook_confirm', 'Confirm booking')}
                              </button>
                            </td>
                          </tr>
                          {open && (
                            <tr style={{ background: '#f8fafc' }}>
                              <td colSpan={6} style={{ padding: '14px 16px' }}>
                                <NetRateConfirmForm bookingId={h.bookingId} sequence={h.sequence} parkedCost={h.parkedCost}
                                  currency={h.currency} canIssueRefunds={canIssueRefunds}
                                  onDone={() => { setOpenHotelKey(null); load() }} t={t} fmtMoney={money} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {rounds.length > 0 && (
          <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>{t('sp_tobook_teetimes_heading', 'Tee times to book')}</h3>
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={thc}>{t('sp_tobook_col_course', 'Course')}</th>
                <th style={thc}>{t('sp_tobook_col_customer', 'Customer')}</th>
                <th style={thc}>{t('sp_tobook_col_play_date', 'Play date')}</th>
                <th style={thc}>{t('sp_tobook_col_requested', 'Requested time')}</th>
                <th style={thc}>{t('sp_tobook_col_golfers', 'Golfers')}</th>
                <th style={thc}>{t('sp_tobook_col_status', 'Status')}</th>
                <th style={{ ...thc, textAlign: 'right' }}>{t('sp_tobook_col_paid', 'Paid')}</th>
                <th style={thc}></th>
              </tr>
            </thead>
            <tbody>
              {rounds.map(r => {
                const awaiting = r.status === 'awaiting_confirmation'
                const key = rowKey(r)
                const open = openId === key
                return (
                  <Fragment key={key}>
                    <tr style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.courseName || '—'}</div>
                        {r.quoteNumber && <div style={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'monospace' }}>{r.quoteNumber}{r.quoteName ? ` · ${r.quoteName}` : ''}</div>}
                      </td>
                      <td style={{ ...td, color: '#64748b' }}>{r.customer || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.playDate)}</td>
                      <td style={{ ...td, color: '#64748b', whiteSpace: 'nowrap' }}>{r.requestedTeeTime || '—'}</td>
                      <td style={{ ...td, color: '#334155', whiteSpace: 'nowrap' }}>{r.golfers != null ? r.golfers : '—'}</td>
                      <td style={td}>
                        {awaiting ? (
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
                              {t('sp_tobook_status_awaiting', 'Book by hand')}
                            </span>
                            {r.source === 'quote' && (
                              <span title={t('sp_tobook_deposit_tag_help', 'Paid by deposit or split — no booking record yet; confirm it here.')}
                                style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: '#f3e8ff', border: '1px solid #e9d5ff', color: '#6d28d9' }}>
                                {t('sp_tobook_deposit_tag', 'Deposit/split')}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}
                            title={r.bookAfterDate ? `${t('sp_tobook_auto_on', 'Auto-books on')} ${fmtDate(r.bookAfterDate)}` : ''}>
                            {t('sp_tobook_status_deferred', 'Auto-books when the course opens')}
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(r.paid, r.currency)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button onClick={() => (open ? setOpenId(null) : openConfirm(r))}
                          style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #0f172a', borderRadius: 6, background: open ? '#fff' : '#0f172a', color: open ? '#0f172a' : '#fff', cursor: 'pointer' }}>
                          {open ? t('sp_cancel', 'Cancel') : t('sp_tobook_confirm', 'Confirm booking')}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={8} style={{ padding: '14px 16px' }}>
                          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10, maxWidth: 680, lineHeight: 1.5 }}>
                            {r.source === 'quote'
                              ? t('sp_tobook_confirm_help_quote', "Enter the tee time you secured with the course (and the course's confirmation number, if any). This marks the round booked on the trip.")
                              : t('sp_tobook_confirm_help', "Enter the tee time you secured with the course (and the course's confirmation number, if any). This marks the round booked and emails the customer.")}
                          </div>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: 12, color: '#475569', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {t('sp_tobook_teetime_label', 'Confirmed tee time')}
                              <input value={form.teeTime} onChange={e => setForm(f => ({ ...f, teeTime: e.target.value }))} placeholder="8:10 AM"
                                style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 160 }} />
                            </label>
                            <label style={{ fontSize: 12, color: '#475569', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {t('sp_tobook_ref_label', 'Course confirmation # (optional)')}
                              <input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))} placeholder="ABC123"
                                style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 220 }} />
                            </label>
                            <button onClick={() => confirm(r)} disabled={busy === key}
                              style={{ padding: '9px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#16a34a', color: '#fff', cursor: busy === key ? 'default' : 'pointer', opacity: busy === key ? 0.6 : 1 }}>
                              {busy === key ? t('sp_saving', 'Saving…') : t('sp_tobook_mark_booked', 'Mark booked')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
          </div>
          )}
        </>
      )}
    </ResellerShell>
  )
}
